import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { Copy, Eye, EyeOff, RefreshCw, Shield } from 'lucide-react';
import { fetchMerchantDetails, requestLiveKey, fetchLiveKeyRequests, regenerateTestKey } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import Modal from '../components/ui/Modal';
import { PERMISSIONS, usePermissions } from '../utils/permissions';

const ApiCredentialsPage = () => {
  const { user } = useAuthStore();
  const [keys, setKeys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  // const [isUpdatingWhitelist, setIsUpdatingWhitelist] = useState(false);
  const [env, setEnv] = useState('test');

  const { can } = usePermissions();
  const isAuthorizedForActions = can(PERMISSIONS.MANAGE_API_KEYS);

  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [liveKeyRequests, setLiveKeyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  /* const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(whitelistSchema),
    mode: 'onChange',
    defaultValues: { ipWhitelist: '' },
  }); */

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const response = await fetchLiveKeyRequests();
      setLiveKeyRequests(response.data.data || []);
    } catch (error) {
      console.error('Failed to load live key requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  const loadKeys = useCallback(async () => {
    if (!user?.merchantId) return;
    setLoading(true);
    try {
      const response = await fetchMerchantDetails(user.merchantId);
      const data = response.data.data;

      setKeys(data);
      if (data?.whitelistedIps) {
        // setValue('ipWhitelist', data.whitelistedIps.join(', '));
      }

      loadRequests();
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [user?.merchantId, loadRequests]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCopy = (text) => {
    if (!text) return toast.error('No key to copy');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleRollKeys = async () => {
    if (env === 'test' && !isAuthorizedForActions) {
      return toast.error('Only Owners or Developers with management permissions can regenerate test keys');
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to regenerate your ${env} API keys. This will invalidate your current keys immediately!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0F172A',
      cancelButtonColor: '#e2e8f0',
      confirmButtonText: 'Yes, regenerate!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        confirmButton: 'px-4 py-2 rounded-lg font-semibold',
        cancelButton: 'px-4 py-2 rounded-lg font-semibold text-slate-600'
      }
    });

    if (!result.isConfirmed) return;

    setIsRolling(true);
    try {
      if (env === 'test') {
        await regenerateTestKey({
          merchantId: user.merchantId,
          userId: user.id
        });
      }
      toast.success('API keys regenerated successfully');
      loadKeys();
    } catch (error) {
      toast.error('Failed to regenerate API keys');
    } finally {
      setIsRolling(false);
    }
  };

  const handleRequestLiveKey = async () => {
    if (!requestReason.trim()) {
      return toast.error('Please provide a reason for the request');
    }

    setIsSubmittingRequest(true);
    try {
      await requestLiveKey({
        email: user.email,
        merchantId: user.merchantId,
        reason: requestReason,
        role: user.role,
        userId: user.id
      });
      toast.success('Live key request submitted successfully');
      setIsRequestModalOpen(false);
      setRequestReason('');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit live key request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const currentKey = env === 'test' ? keys?.testApiKey : keys?.liveApiKey;
  const currentActive = env === 'test' ? keys?.testKeyActive : keys?.liveKeyActive;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">API Credentials</h2>
          <p className="text-sm text-slate-500 mt-1">Manage your API keys and security settings for integration.</p>
        </div>

        {/* Environment Toggle */}
        <div className="flex bg-slate-100 p-1 rounded-lg self-start">
          <button
            onClick={() => setEnv('test')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${env === 'test'
              ? 'bg-white text-primary shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            Test Mode
          </button>
          <button
            onClick={() => setEnv('live')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${env === 'live'
              ? 'bg-primary text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            Live Mode
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className={env === 'live' ? "text-primary" : "text-amber-500"} size={20} />
                <div>
                  <CardTitle>{env === 'live' ? 'Live' : 'Test'} API Keys</CardTitle>
                  <CardDescription>
                    {env === 'live'
                      ? 'Credentials for real transactions and data.'
                      : 'Credentials for testing and development.'}
                  </CardDescription>
                </div>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${currentActive
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-100 text-slate-500'
                }`}>
                {currentActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-700">Merchant ID</label>
                <div className="flex bg-slate-50 border border-slate-200 rounded-lg p-3 items-center justify-between">
                  <code className="text-sm text-slate-600 font-mono truncate mr-4">
                    {keys?.merchantId}
                  </code>
                  <button onClick={() => handleCopy(keys?.merchantId)} className="text-slate-400 hover:text-primary transition-colors shrink-0">
                    <Copy size={16} />
                  </button>
                </div>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-700">API Key</label>
                <div className="flex bg-slate-50 border border-slate-200 rounded-lg p-3 items-center justify-between">
                  <div className="flex items-center gap-2 truncate mr-4">
                    <code className="text-sm text-slate-600 font-mono">
                      {showSecret ? (currentKey || 'Not generated') : '********_***************************'}
                    </code>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-slate-400 hover:text-primary transition-colors"
                      title={showSecret ? "Hide" : "Reveal"}
                    >
                      {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button onClick={() => handleCopy(currentKey)} className="text-slate-400 hover:text-primary transition-colors">
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {env === 'test' ? (
              currentKey && isAuthorizedForActions && (
                <div className="pt-4 border-t border-slate-100 flex justify-end">
                  <Button
                    variant="secondary"
                    className="gap-2 text-sm"
                    onClick={handleRollKeys}
                    isLoading={isRolling}
                  >
                    <RefreshCw size={14} />
                    Regenerate Test Keys
                  </Button>
                </div>
              )
            ) : (
              isAuthorizedForActions && (
                <div className="pt-4 border-t border-slate-100 flex justify-end">
                  <Button
                    variant="primary"
                    className="gap-2 text-sm"
                    onClick={() => setIsRequestModalOpen(true)}
                    disabled={keys?.liveKeyActive}
                  >
                    <RefreshCw size={14} />
                    {keys?.liveKeyActive ? 'Live Key Active' : 'Request Live Key'}
                  </Button>
                </div>
              )
            )}
          </CardContent>
        </Card>

        {env === 'live' && (
          <Card>
            <CardHeader>
              <CardTitle>Live Key Requests</CardTitle>
              <CardDescription>View the status of your live key generation requests.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingRequests ? (
                <div className="py-8 flex justify-center">
                  <Spinner size="sm" />
                </div>
              ) : liveKeyRequests.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-slate-100">
                        <th className="pb-3 font-medium text-slate-500">Date</th>
                        <th className="pb-3 font-medium text-slate-500">Reason</th>
                        <th className="pb-3 font-medium text-slate-500 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {liveKeyRequests.map((req, idx) => (
                        <tr key={idx}>
                          <td className="py-3 text-slate-600">
                            {new Date(req.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-3 text-slate-600 truncate max-w-[200px]" title={req.reason}>
                            {req.reason}
                          </td>
                          <td className="py-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${req.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                              req.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                              {req.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-slate-500 italic">
                  No requests found.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Whitelisted IPs - Removed temporarily */}
        {/* <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="text-primary" size={20} />
              <div>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Restrict API access to specific IP addresses.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit(onWhitelistSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Whitelisted IPs</label>
                <textarea
                  className={`w-full p-3 bg-slate-50 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${errors.ipWhitelist ? 'border-red-500' : 'border-slate-200'}`}
                  rows={3}
                  placeholder="Enter IP addresses separated by commas (e.g. 1.2.3.4, 5.6.7.8)"
                  {...register('ipWhitelist')}
                />
                {errors.ipWhitelist && <p className="text-xs text-red-500 mt-1">{errors.ipWhitelist.message}</p>}
                <p className="text-[10px] text-slate-400 italic">Use a comma to separate multiple Whitelisted IPs</p>
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit" isLoading={isUpdatingWhitelist}>Save Whitelist</Button>
              </div>
            </form>
          </CardContent>
        </Card> */}
      </div>

      <Modal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        title="Request Live API Key"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setIsRequestModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestLiveKey} isLoading={isSubmittingRequest}>Submit Request</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Please provide a reason for requesting a live API key. Our team will review your request and get back to you shortly.
          </p>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Reason for Request</label>
            <textarea
              className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              rows={4}
              placeholder="e.g., We are ready to move to production for our e-commerce integration."
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApiCredentialsPage;
